export const ONBOARDING_SIGNUP_TIMING_EXPERIMENT_KEY = "onboarding_signup_timing_v1";

export const ONBOARDING_SIGNUP_TIMING_VARIANTS = {
  control: "control",
  signupFirst: "signup_before_demo",
} as const;

export const ONBOARDING_SIGNUP_TIMING_QUERY_KEY = "onboarding_signup_timing";
export const ONBOARDING_SIGNUP_TIMING_EXPOSURE_STORAGE_KEY =
  "chickytutor:experiment:onboarding-signup-timing:v1";

export type OnboardingSignupTimingVariant =
  (typeof ONBOARDING_SIGNUP_TIMING_VARIANTS)[keyof typeof ONBOARDING_SIGNUP_TIMING_VARIANTS];

export type OnboardingSignupTimingExperimentProperties = {
  experiment_key: typeof ONBOARDING_SIGNUP_TIMING_EXPERIMENT_KEY;
  experiment_variant: OnboardingSignupTimingVariant;
  experiment_assignment_id?: string;
};

export type OnboardingSignupTimingExposure = {
  properties: Required<OnboardingSignupTimingExperimentProperties>;
  newlyExposed: boolean;
};

const EXPOSURE_TTL_MS = 8 * 24 * 60 * 60 * 1000;

type StoredExposure = Required<OnboardingSignupTimingExperimentProperties> & {
  exposed_at_ms: number;
};

export function parseOnboardingSignupTimingVariant(
  value: unknown
): OnboardingSignupTimingVariant | null {
  return value === ONBOARDING_SIGNUP_TIMING_VARIANTS.control ||
    value === ONBOARDING_SIGNUP_TIMING_VARIANTS.signupFirst
    ? value
    : null;
}

function createAssignmentId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `onboarding_signup_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function readStoredExposure(storage: Storage, nowMs: number): StoredExposure | null {
  try {
    const parsed = JSON.parse(
      storage.getItem(ONBOARDING_SIGNUP_TIMING_EXPOSURE_STORAGE_KEY) || "null"
    ) as Partial<StoredExposure> | null;
    const variant = parseOnboardingSignupTimingVariant(parsed?.experiment_variant);
    if (
      !parsed ||
      parsed.experiment_key !== ONBOARDING_SIGNUP_TIMING_EXPERIMENT_KEY ||
      !variant ||
      typeof parsed.experiment_assignment_id !== "string" ||
      !parsed.experiment_assignment_id.trim() ||
      typeof parsed.exposed_at_ms !== "number" ||
      nowMs < parsed.exposed_at_ms ||
      nowMs - parsed.exposed_at_ms > EXPOSURE_TTL_MS
    ) {
      return null;
    }

    return {
      experiment_key: ONBOARDING_SIGNUP_TIMING_EXPERIMENT_KEY,
      experiment_variant: variant,
      experiment_assignment_id: parsed.experiment_assignment_id,
      exposed_at_ms: parsed.exposed_at_ms,
    };
  } catch {
    return null;
  }
}

export function clearOnboardingSignupTimingExposure(storage?: Storage | null): void {
  try {
    const targetStorage =
      storage === undefined
        ? typeof window === "undefined"
          ? null
          : window.localStorage
        : storage;
    targetStorage?.removeItem(ONBOARDING_SIGNUP_TIMING_EXPOSURE_STORAGE_KEY);
  } catch {
    // Stale attribution must never block the unexposed control path.
  }
}

export function recordOnboardingSignupTimingExposure({
  variant,
  storage = typeof window === "undefined" ? null : window.localStorage,
  nowMs = Date.now(),
}: {
  variant: OnboardingSignupTimingVariant;
  storage?: Storage | null;
  nowMs?: number;
}): OnboardingSignupTimingExposure {
  const existing = storage ? readStoredExposure(storage, nowMs) : null;
  if (existing?.experiment_variant === variant) {
    const { exposed_at_ms: _exposedAtMs, ...properties } = existing;
    return { properties, newlyExposed: false };
  }

  const properties: Required<OnboardingSignupTimingExperimentProperties> = {
    experiment_key: ONBOARDING_SIGNUP_TIMING_EXPERIMENT_KEY,
    experiment_variant: variant,
    experiment_assignment_id: createAssignmentId(),
  };

  if (storage) {
    try {
      storage.setItem(
        ONBOARDING_SIGNUP_TIMING_EXPOSURE_STORAGE_KEY,
        JSON.stringify({ ...properties, exposed_at_ms: nowMs } satisfies StoredExposure)
      );
    } catch {
      // Experiment attribution must never block onboarding.
    }
  }

  return { properties, newlyExposed: true };
}

export function getOnboardingSignupTimingEventProperties({
  storage = typeof window === "undefined" ? null : window.localStorage,
  search = typeof window === "undefined" ? "" : window.location.search,
  nowMs = Date.now(),
}: {
  storage?: Storage | null;
  search?: string;
  nowMs?: number;
} = {}): OnboardingSignupTimingExperimentProperties | Record<string, never> {
  if (storage) {
    const exposure = readStoredExposure(storage, nowMs);
    if (exposure) {
      const { exposed_at_ms: _exposedAtMs, ...properties } = exposure;
      return properties;
    }
  }

  const returnVariant = parseOnboardingSignupTimingVariant(
    new URLSearchParams(search).get(ONBOARDING_SIGNUP_TIMING_QUERY_KEY)
  );
  return returnVariant
    ? {
        experiment_key: ONBOARDING_SIGNUP_TIMING_EXPERIMENT_KEY,
        experiment_variant: returnVariant,
      }
    : {};
}
